import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Post, PostSummary, TocItem } from '@/types'

import { useRef } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { getPost, getPostSlugs, getRelatedPosts } from '@/lib/content'
import { useScrollFrame } from '@/hooks/use-scroll-frame'
import { useScrollSpy } from '@/hooks/use-scroll-spy'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { Hashtag } from '@/components/kit/hashtag'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { Reveal } from '@/components/kit/reveal'
import { Spark } from '@/components/kit/spark'
import RichText from '@/components/cms/rich-text'
import { NewsletterBlock } from '@/components/blog/newsletter-block'
import { PostCard } from '@/components/blog/post-card'
import { ShareButtons } from '@/components/blog/share-buttons'

interface ArticleProps {
    post: Post
    related: Array<PostSummary>
}

const ReadingMeter: FC = () => {
    const valueRef = useRef<HTMLSpanElement>(null)
    const barRef = useRef<HTMLSpanElement>(null)

    useScrollFrame(() => {
        const body = document.getElementById('article-body')

        if (!body || !valueRef.current || !barRef.current) return

        const rect = body.getBoundingClientRect()
        const distance = rect.height - window.innerHeight * 0.6
        const progress = distance > 0 ? Math.min(1, Math.max(0, (window.innerHeight * 0.4 - rect.top) / distance)) : 0

        valueRef.current.textContent = `${Math.round(progress * 100)}%`
        barRef.current.style.transform = `scaleX(${progress.toFixed(4)})`
    })

    return (
        <div className='mt-8'>
            <div className='flex items-center justify-between font-mono text-[11px] uppercase tracking-[0.14em] text-stone'>
                <span>How far in</span>
                <span ref={valueRef}>0%</span>
            </div>
            <span className='mt-2 block h-1 overflow-hidden rounded-full bg-ink/[0.08]'>
                <span
                    ref={barRef}
                    className='block h-full origin-left rounded-full bg-ember'
                    style={{ transform: 'scaleX(0)' }}
                />
            </span>
        </div>
    )
}

const TableOfContents: FC<{ items: Array<TocItem> }> = ({ items }) => {
    const active = useScrollSpy(items.map(item => item.id), '-15% 0px -70% 0px')

    if (!items.length) return null

    return (
        <nav aria-label='On this page'>
            <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>In this story</p>
            <ol className='mt-4 flex flex-col border-l border-ink/10'>
                {items.map(item => {
                    const current = item.id === active

                    return (
                        <li key={item.id}>
                            <a
                                href={`#${item.id}`}
                                aria-current={current ? 'location' : undefined}
                                className={cn(
                                    '-ml-px block border-l-2 py-2 pl-4 text-sm leading-snug transition-colors duration-300',
                                    current ? 'border-ember font-medium text-ink' : 'border-transparent text-stone hover:text-ink'
                                )}
                            >
                                {item.text}
                            </a>
                        </li>
                    )
                })}
            </ol>
        </nav>
    )
}

const Article: FC<ArticleProps> = ({ post, related }): ReactNode => {
    const author = post.author

    return (
        <Layout>
            <Seo
                title={post.title}
                description={post.excerpt ?? `${post.title}, from the Updraft journal.`}
                ogImage={post.cover ?? undefined}
                type='article'
            />

            <article>
                <header className='mx-auto max-w-[1400px] px-5 pt-36 md:px-10 md:pt-44'>
                    <div className='max-w-[1050px]'>
                        <Reveal>
                            <nav
                                aria-label='Breadcrumb'
                                className='flex flex-wrap items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-stone'
                            >
                                <PageLink
                                    href='/journal'
                                    className='text-ink underline decoration-ember decoration-2 underline-offset-4'
                                >
                                    Journal
                                </PageLink>
                                {post.category && (
                                    <>
                                        <Spark className='size-2 text-ember' />
                                        <span data-mdlfy-cms={`categories:${post.category.id}:name`}>{post.category.name}</span>
                                    </>
                                )}
                            </nav>
                        </Reveal>

                        <Reveal delay={100}>
                            <h1 className='mt-6 text-[clamp(2.6rem,6.4vw,6rem)] font-bold leading-[0.95] tracking-[-0.04em]'>
                                <span data-mdlfy-cms={`posts:${post.id}:title`}>{post.title}</span>
                            </h1>
                        </Reveal>

                        {post.excerpt && (
                            <Reveal delay={200}>
                                <p
                                    className='mt-7 max-w-3xl text-xl leading-relaxed text-stone md:text-2xl'
                                    data-mdlfy-cms={`posts:${post.id}:excerpt`}
                                >
                                    {post.excerpt}
                                </p>
                            </Reveal>
                        )}

                        <Reveal
                            delay={300}
                            className='mt-10 flex flex-wrap items-center gap-x-8 gap-y-5 border-t border-ink/10 pt-6'
                        >
                            {author && (
                                <div className='flex items-center gap-3'>
                                    {author.avatar && (
                                        <Photo
                                            src={author.avatar}
                                            alt={author.avatar_alt ?? ''}
                                            sizes='48px'
                                            position='50% 25%'
                                            className='size-12 rounded-full'
                                        />
                                    )}
                                    <div>
                                        <p className='text-[15px] font-semibold'>
                                            <span data-mdlfy-cms={`authors:${author.id}:name`}>{author.name}</span>
                                        </p>
                                        {author.role && (
                                            <p
                                                className='text-sm text-stone'
                                                data-mdlfy-cms={`authors:${author.id}:role`}
                                            >
                                                {author.role}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}

                            <dl className='flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-[0.12em] text-stone'>
                                {post.published_at && (
                                    <div>
                                        <dt className='sr-only'>Published</dt>
                                        <dd>
                                            <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
                                        </dd>
                                    </div>
                                )}
                                {post.read_minutes && (
                                    <div>
                                        <dt className='sr-only'>Reading time</dt>
                                        <dd>
                                            <span data-mdlfy-cms={`posts:${post.id}:read_minutes`}>{post.read_minutes}</span> min read
                                        </dd>
                                    </div>
                                )}
                            </dl>

                            {post.hashtags.length > 0 && (
                                <div className='flex flex-wrap gap-2'>
                                    {post.hashtags.map(tag => (
                                        <Hashtag
                                            key={tag}
                                            tag={tag}
                                            tone='outline'
                                        />
                                    ))}
                                </div>
                            )}
                        </Reveal>
                    </div>
                </header>

                {post.cover && (
                    <Reveal
                        delay={200}
                        className='mx-auto mt-12 max-w-[1400px] px-5 md:mt-16 md:px-10'
                    >
                        <Photo
                            src={post.cover}
                            alt={post.cover_alt ?? ''}
                            sizes='(min-width: 1400px) 1320px, 100vw'
                            preload
                            grain
                            className='aspect-[16/10] rounded-[1.75rem] md:aspect-[16/8] md:rounded-[2.5rem]'
                        />
                    </Reveal>
                )}

                <div className='mx-auto mt-14 grid max-w-[1400px] gap-10 px-5 md:mt-20 md:px-10 lg:grid-cols-12'>
                    <aside className='hidden lg:col-span-3 lg:block'>
                        <div className='sticky top-28'>
                            <TableOfContents items={post.toc} />
                            <ReadingMeter />
                            <div className='mt-8 border-t border-ink/10 pt-6'>
                                <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Share</p>
                                <ShareButtons
                                    title={post.title}
                                    className='mt-4'
                                />
                            </div>
                        </div>
                    </aside>

                    <div className='min-w-0 lg:col-span-7 lg:col-start-5'>
                        {post.toc.length > 0 && (
                            <details className='group mb-10 rounded-[1.5rem] border border-black/5 bg-paper p-5 lg:hidden'>
                                <summary className='flex cursor-pointer list-none items-center justify-between font-mono text-xs uppercase tracking-[0.16em] text-stone [&::-webkit-details-marker]:hidden'>
                                    On this page
                                    <ChevronDown className='size-4 transition-transform group-open:rotate-180' />
                                </summary>
                                <ol className='mt-4 flex flex-col gap-1'>
                                    {post.toc.map(item => (
                                        <li key={item.id}>
                                            <a
                                                href={`#${item.id}`}
                                                className='block py-1.5 text-[15px] text-ink'
                                            >
                                                {item.text}
                                            </a>
                                        </li>
                                    ))}
                                </ol>
                            </details>
                        )}

                        <div id='article-body'>
                            <RichText html={post.body} />
                        </div>

                        <div className='mt-14 flex flex-col gap-6 border-t border-ink/10 pt-8 sm:flex-row sm:items-center sm:justify-between'>
                            <p className='font-heading text-xl font-bold tracking-[-0.03em]'>
                                Know someone who needs this? Send it their way.
                            </p>
                            <ShareButtons title={post.title} />
                        </div>

                        {author && (
                            <section
                                aria-label='About the author'
                                className='mt-12 flex flex-col gap-6 rounded-[2rem] border border-black/5 bg-paper p-7 sm:flex-row sm:items-start md:p-9'
                            >
                                {author.avatar && (
                                    <Photo
                                        src={author.avatar}
                                        alt={author.avatar_alt ?? ''}
                                        sizes='96px'
                                        position='50% 25%'
                                        className='size-24 shrink-0 rounded-full'
                                    />
                                )}
                                <div>
                                    <p className='font-mono text-[11px] uppercase tracking-[0.16em] text-stone'>Filed by</p>
                                    <p className='mt-2 font-heading text-2xl font-bold tracking-[-0.04em]'>
                                        <span data-mdlfy-cms={`authors:${author.id}:name`}>{author.name}</span>
                                    </p>
                                    {author.role && (
                                        <p
                                            className='mt-0.5 text-stone'
                                            data-mdlfy-cms={`authors:${author.id}:role`}
                                        >
                                            {author.role}
                                        </p>
                                    )}
                                    {author.bio && (
                                        <p
                                            className='mt-4 max-w-xl text-[15px] leading-relaxed text-ink/80'
                                            data-mdlfy-cms={`authors:${author.id}:bio`}
                                        >
                                            {author.bio}
                                        </p>
                                    )}
                                </div>
                            </section>
                        )}
                    </div>
                </div>
            </article>

            {related.length > 0 && (
                <section
                    aria-labelledby='related-title'
                    className='mx-auto max-w-[1400px] px-5 pt-24 md:px-10 md:pt-32'
                >
                    <div className='flex items-end justify-between gap-6'>
                        <h2
                            id='related-title'
                            className='text-[clamp(2.25rem,4.6vw,4rem)] font-bold leading-[0.95] tracking-[-0.035em]'
                        >
                            More from the <span className='font-serif font-normal italic tracking-[-0.01em]'>desk</span>
                        </h2>
                        <PageLink
                            href='/journal'
                            className='shrink-0 text-sm font-medium underline decoration-ember decoration-2 underline-offset-4'
                        >
                            Every story
                        </PageLink>
                    </div>

                    <div className='mt-12 grid gap-x-6 gap-y-14 md:grid-cols-2 lg:grid-cols-3'>
                        {related.map((item, index) => (
                            <Reveal
                                key={item.id}
                                delay={index * 90}
                            >
                                <PostCard post={item} />
                            </Reveal>
                        ))}
                    </div>
                </section>
            )}

            <div className='mx-auto max-w-[1400px] px-5 pt-24 md:px-10'>
                <NewsletterBlock source='article' />
            </div>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getPostSlugs()

        return { paths: slugs.map(slug => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Article paths could not be loaded:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ArticleProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!slug) return { notFound: true, revalidate: 30 }

    try {
        const post = await getPost(slug)

        if (!post) return { notFound: true, revalidate: 30 }

        const related = await getRelatedPosts(post, 3)

        return { props: { post, related }, revalidate: 60 }
    }
    catch (error) {
        console.error(`Article "${slug}" could not be loaded:`, error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 15 }
    }
}

export default Article
